import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getPersonalAccidentQuote } from "../../../../api/personalAccidentServices";
import redflag from "../../../../assets/images/bahrainsmlogo.svg";
import Input from "../../../../components/common/Input/Input";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import { getLoggedInUserDataFromLocalStorage } from "../../../../config/config";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePersonalAccidentFlow from "../../../../hooks/usePersonalAccidentFlow";
import usePersonalAccidentFormData from "../../../../hooks/usePersonalAccidentFormData";
import useValidation from "../../../../hooks/useValidation";
import SignInModal from "../../../../pages/motorInsurancePages/newMotorPolicy/planPages/signInModal";
import {
  fetchOccupationsList,
  selectOccupations,
  selectOccupationsLoader,
} from "../../../../redux/features/constants/occupationsListSlice";
import {
  selectLoginTokenInfo,
  setLoginTokenInfo,
} from "../../../../redux/features/loginRegisterSlice";
import { selectPersonalAccidentForm } from "../../../../redux/features/personalAccidentFormSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import { currencyFormatter } from "../../../../utils";
import TravelSummaryModal from "../../../newTravelPolicy/modals/travelSummaryModal";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import PersonalAccidentJourneyLayout from "../../layouts/PersonalAccidentJourneyLayout";

const PERSONAL_ACCIDENT_BASIC_INFO = "PERSONAL_ACCIDENT_BASIC_INFO";

const validationConfig = [
  {
    name: "policy_limit",
    validations: ["required"],
    max_value: 25000,
  },
  {
    name: "occupation",
    validations: ["required"],
  },
];

const PersonalInfo = () => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const [values, setValues] = useState({});
  const { errors, validateField, validateFields } = useValidation();
  const { handleNextSubstep } = usePersonalAccidentFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [policyLimitError, setPolicyLimitError] = useState("");
  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // Quote state
  const [quoteData, setQuoteData] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteHasFailed, setQuoteHasFailed] = useState(false);

  // Get occupations data from Redux store
  const occupationsData = useSelector(selectOccupations);
  const occupationsLoading = useSelector(selectOccupationsLoader);

  // Get all form data to check if we have basic info
  const personalAccidentFormData = useSelector(selectPersonalAccidentForm);

  // Get profile data for logged-in users
  const profileData = useSelector(selectProfileData);

  // Determine if user is logged in
  const isLoggedIn = Boolean(loginTokenInfo);

  const { savePersonalAccidentDataInRedux } = usePersonalAccidentFormData(
    PERSONAL_ACCIDENT_BASIC_INFO,
    setValues
  );

  // Fetch occupations data on component mount
  useEffect(() => {
    if (!occupationsData) {
      dispatch(fetchOccupationsList());
    }
  }, [dispatch, occupationsData]);

  // Fetch profile data if user is logged in but profile data is missing
  useEffect(() => {
    if (isLoggedIn && !profileData) {
      console.log("Fetching profile data for logged-in user");
      dispatch(fetchProfileInfo());
    }
  }, [isLoggedIn, profileData, dispatch]);

  // Auto-fetch quote when both policy limit and occupation are selected - WITH VALIDATION AND DEBOUNCE
  useEffect(() => {
    if (values.policy_limit && values.occupation && !isLoadingQuote) {
      // Debounce the API call - wait 3 seconds after last change
      const debounceTimer = setTimeout(() => {
        // Validate policy limit before making API call - handle both commas and spaces
        const numericValue = values.policy_limit.replace(/[,\s]/g, "");
        const numericAmount = parseFloat(numericValue);
        
        if (numericAmount < 1000) {
          setPolicyLimitError(
            translate(
              "Policy limit should be BHD 1,000 or higher.",
              "يجب ألا يقل مبلغ التأمين عن 1,000 د.ب."
            )
          );
          return;
        }

        if (numericAmount > 25000) {
          console.log("Policy limit exceeds 25000, showing error instead of fetching quote");
          setPolicyLimitError(
            translate(
              "Policy limit cannot exceed BHD 25,000.",
              "مبلغ التأمين لا يمكن أن يتجاوز 25,000 دينار بحريني."
            )
          );
          // Don't fetch quote if validation fails
          return;
        }
        
        // Clear any previous error
        setPolicyLimitError("");
        
        console.log("Both policy limit and occupation selected, fetching quote after debounce");
        fetchQuoteForPersonalAccident();
      }, 3000); // 3 second debounce
      
      // Cleanup function to cancel the timeout if values change again
      return () => {
        console.log("Clearing debounce timer due to value change");
        clearTimeout(debounceTimer);
      };
    }
  }, [values.policy_limit, values.occupation]);

  // Multiple login detection methods
  useEffect(() => {
    if (isWaitingForLogin && loginTokenInfo) {
      console.log("Login detected via token change, proceeding to next step");
      setIsWaitingForLogin(false);
      setShowLoginModal(false);

      // Save form data and proceed
      savePersonalAccidentDataInRedux(values);
      handleNextSubstep();
    }
  }, [
    loginTokenInfo,
    isWaitingForLogin,
    values,
    savePersonalAccidentDataInRedux,
    handleNextSubstep,
  ]);

  // Additional effect to handle when user logs in from outside and returns to page
  useEffect(() => {
    if (isLoggedIn && showLoginModal && isWaitingForLogin) {
      console.log(
        "Login detected via isLoggedIn change, proceeding to next step"
      );
      setShowLoginModal(false);
      setIsWaitingForLogin(false);

      // Save form data and proceed
      savePersonalAccidentDataInRedux(values);
      handleNextSubstep();
    }
  }, [
    isLoggedIn,
    showLoginModal,
    isWaitingForLogin,
    values,
    savePersonalAccidentDataInRedux,
    handleNextSubstep,
  ]);

  // Listen for storage changes to detect login from popup window
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user" || e.key === "loginToken") {
        console.log("Storage change detected, checking for login");
        const tokenInfo = getLoggedInUserDataFromLocalStorage();
        if (tokenInfo) {
          dispatch(setLoginTokenInfo(tokenInfo));
          setIsWaitingForLogin(false);
          setShowLoginModal(false);

          // Save form data and proceed
          savePersonalAccidentDataInRedux(values);
          handleNextSubstep();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, values, savePersonalAccidentDataInRedux, handleNextSubstep]);

  // Polling mechanism as fallback
  useEffect(() => {
    if (!isWaitingForLogin) return;

    const pollForLogin = setInterval(() => {
      const tokenInfo = getLoggedInUserDataFromLocalStorage();
      if (tokenInfo) {
        console.log("Login detected via polling, proceeding to next step");
        dispatch(setLoginTokenInfo(tokenInfo));
        setIsWaitingForLogin(false);
        setShowLoginModal(false);

        // Save form data and proceed
        savePersonalAccidentDataInRedux(values);
        handleNextSubstep();
        clearInterval(pollForLogin);
      }
    }, 1000);

    return () => clearInterval(pollForLogin);
  }, [
    isWaitingForLogin,
    dispatch,
    values,
    savePersonalAccidentDataInRedux,
    handleNextSubstep,
  ]);

  // Function to fetch quote for Personal Accident
  const fetchQuoteForPersonalAccident = async () => {
    console.log("=== Fetching Personal Accident Quote ===");
    console.log("Is logged in:", isLoggedIn);
    console.log("Profile data:", profileData);

    setIsLoadingQuote(true);
    setQuoteHasFailed(false);

    try {
      // Calculate age from basic info or profile
      let age;
      let partyCode;

      if (isLoggedIn && profileData) {
        // LOGGED-IN USER: Use profile data
        console.log("Using profile data for logged-in user");
        console.log("Full profile data:", profileData);
        console.log(
          "Profile data keys:",
          profileData ? Object.keys(profileData) : "No profile data"
        );
        console.log("Profile DOB field check:", {
          DOB: profileData?.DOB,
          DateOfBirth: profileData?.DateOfBirth,
          dob: profileData?.dob,
          dateOfBirth: profileData?.dateOfBirth,
        });
        console.log("LoginTokenInfo:", loginTokenInfo);

        // Calculate age from profile DOB - try multiple field names
        const dobField = profileData.DOB || profileData.DateOfBirth || profileData.dob || profileData.dateOfBirth;

        if (dobField) {
          console.log("Profile DOB:", dobField);
          try {
            const birthDate = new Date(dobField);
            const today = new Date();

            // Validate the birth date
            if (isNaN(birthDate.getTime())) {
              console.error("Invalid DOB format:", dobField);
              age = 0; // Default to 0 if invalid date
            } else {
              age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (
                monthDiff < 0 ||
                (monthDiff === 0 && today.getDate() < birthDate.getDate())
              ) {
                age--;
              }
              console.log("Calculated age from profile:", age);
            }
          } catch (error) {
            console.error("Error calculating age from DOB:", error);
            age = 0; // Default to 0 if calculation fails
          }
        } else {
          console.error("No DOB found in profile data! Setting age to 0");
          console.error("Checked fields: DOB, DateOfBirth, dob, dateOfBirth");
          console.error("Available profile fields:", Object.keys(profileData));
          age = 0; // Default to 0 if no DOB
        }

        partyCode = profileData.PartyCode || loginTokenInfo?.partyCode;
        console.log("PartyCode from profile:", profileData.PartyCode);
        console.log("PartyCode from loginToken:", loginTokenInfo?.partyCode);
        console.log("Final partyCode:", partyCode);
      } else if (isLoggedIn && !profileData) {
        // LOGGED-IN USER but profile data not loaded yet
        console.log("User is logged in but profile data is not loaded yet");
        console.log("Is logged in:", isLoggedIn);
        console.log("Profile data:", profileData);
        toast.error(
          translate(
            "Loading profile data. Please wait...",
            "جاري تحميل بيانات الملف الشخصي. يرجى الانتظار..."
          )
        );
        setIsLoadingQuote(false);
        return;
      } else {
        // GUEST USER: Use basic info from form
        console.log("Using basic info for guest user");
        const basicInfoData =
          personalAccidentFormData?.[PERSONAL_ACCIDENT_BASIC_INFO];
        console.log("Basic info data:", basicInfoData);

        if (!basicInfoData?.birthDate) {
          console.error("No birth date found in basic info data!");
          console.error(
            "Available basic info keys:",
            basicInfoData ? Object.keys(basicInfoData) : "No basic info data"
          );
          toast.error(
            translate(
              "Basic information is required to get a quote",
              "المعلومات الأساسية مطلوبة للحصول على عرض أسعار"
            )
          );
          setIsLoadingQuote(false);
          return;
        }

        console.log("Using basic info data for guest user");
        console.log("Basic info data:", basicInfoData);

        // Calculate age from basic info birth date
        const birthDate = new Date(basicInfoData.birthDate);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        // For guest users, use default FALCON partyCode
        partyCode = "FALCON";
      }

      // Remove formatting from policy limit for API - handle both commas and spaces
      const cleanPolicyLimit = values.policy_limit.replace(/[,\s]/g, "");

      console.log("=== Final validation before API call ===");
      console.log("Age:", age, "Type:", typeof age);
      console.log("PartyCode:", partyCode, "Type:", typeof partyCode);
      console.log(
        "Occupation:",
        values.occupation,
        "Type:",
        typeof values.occupation
      );
      console.log(
        "Policy Limit (clean):",
        cleanPolicyLimit,
        "Type:",
        typeof cleanPolicyLimit
      );
      console.log(
        "Token:",
        loginTokenInfo?.access_token ? "Present" : "Missing"
      );

      if (age === undefined || age === null) {
        console.error("=== VALIDATION FAILED ===");
        console.error("Missing age:", age === undefined || age === null);
        console.error("Age value:", age);
        setQuoteHasFailed(true);
        setQuoteData(null);
        toast.error(
          translate(
            "Unable to calculate quote. Missing required information.",
            "غير قادر على حساب عرض الأسعار. معلومات مطلوبة مفقودة."
          )
        );
        return;
      }

      console.log("Making Personal Accident quote API call with data:", {
        age,
        occupation: values.occupation,
        partyCode,
        sumInsured: cleanPolicyLimit,
        token: loginTokenInfo?.access_token || null,
      });

      const response = await getPersonalAccidentQuote({
        age,
        occupation: values.occupation,
        partyCode,
        sumInsured: cleanPolicyLimit,
        token: loginTokenInfo?.access_token || null,
      });

      if (response.success) {
        setQuoteData(response.data);
        setQuoteHasFailed(false);
        console.log("Personal Accident quote success:", response.data);
      } else {
        console.error("Personal Accident quote failed:", response.error);
        setQuoteHasFailed(true);
        setQuoteData(null);

        // Show error toast
        toast.error(
          translate(
            "Unable to calculate premium at the moment. Please try again later.",
            "غير قادر على حساب القسط في الوقت الحالي. يرجى المحاولة مرة أخرى لاحقاً."
          )
        );
      }
    } catch (error) {
      console.error("Personal Accident quote exception:", error);
      setQuoteHasFailed(true);
      setQuoteData(null);

      toast.error(
        translate(
          "Unable to calculate premium at the moment. Please try again later.",
          "غير قادر على حساب القسط في الوقت الحالي. يرجى المحاولة مرة أخرى لاحقاً."
        )
      );
    } finally {
      setIsLoadingQuote(false);
    }
  };

  // Transform occupations data for SelectInput component
  const occupationOptions = React.useMemo(() => {
    if (!occupationsData || !Array.isArray(occupationsData)) return [];

    return occupationsData.map((occupation) => ({
      id: occupation.Code,
      value: occupation.Code.toString(),
      label: translate(occupation.Name, occupation.NameAr),
    }));
  }, [occupationsData, translate]);

  const validateForm = (data) => {
    const formData = data ? data : values;
    let isValid = validateFields(validationConfig, formData);

    // Additional validation for policy limit - handle both commas and spaces
    if (formData.policy_limit) {
      const numericValue = formData.policy_limit.replace(/[,\s]/g, "");
      const numericAmount = parseFloat(numericValue);

      if (numericAmount < 1000) {
        setPolicyLimitError(
          translate(
            "Policy limit should be BHD 1,000 or higher.",
            "يجب ألا يقل مبلغ التأمين عن 1,000 د.ب."
          )
        );
        isValid = false;
      } else if (numericAmount > 25000) {
        setPolicyLimitError(
          translate(
            "Policy limit cannot exceed BHD 25,000.",
            "مبلغ التأمين لا يمكن أن يتجاوز 25,000 دينار بحريني."
          )
        );
        isValid = false;
      }
    }

    // Check if we have policy limit error
    if (policyLimitError) {
      isValid = false;
    }

    return isValid;
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Check if user is logged in
      if (!isLoggedIn) {
        // Show login modal for non-logged in users
        setShowLoginModal(true);
        setIsWaitingForLogin(true);
        return;
      }

      // User is logged in, save data and proceed to next page (contactDetails)
      savePersonalAccidentDataInRedux(values);
      handleNextSubstep();
    }
  };

  const handleLoginSuccess = () => {
    // User successfully logged in from SignInModal
    setShowLoginModal(false);
    setIsWaitingForLogin(false);
    // Save data and proceed to next page
    savePersonalAccidentDataInRedux(values);
    handleNextSubstep();
  };

  const handleSkipLogin = () => {
    console.log("handleSkipLogin called");
    setShowLoginModal(false);
    setIsWaitingForLogin(false);
    // Save data and proceed to next page without login
    savePersonalAccidentDataInRedux(values);
    handleNextSubstep();
  };

  // Handle Show Summary click
  const handleShowSummary = () => {
    console.log("=== Show Summary clicked ===");
    console.log("Quote data:", quoteData);

    // Quote should already be available
    setShowSummaryModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let update = { ...values, [name]: value };
    setValues(update);

    // Clear previous quote data when form values change
    setQuoteData(null);
    setQuoteHasFailed(false);

    if (errors?.[name]) {
      validateForm(update);
    }
  };

  // Format number with commas for policy limit - use consistent en-US formatting
  const formatPolicyLimit = (value) => {
    if (!value) return "";
    const numericValue = value.replace(/[,\s]/g, "");
    if (!numericValue) return "";
    return Number(numericValue).toLocaleString('en-US');
  };

  const handlePolicyLimitChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[,\s]/g, "");

    // Clear previous quote data when form values change
    setQuoteData(null);
    setQuoteHasFailed(false);

    const numericAmount = parseFloat(numericValue) || 0;
    if (numericValue && numericAmount < 1000) {
      setPolicyLimitError(
        translate(
          "Policy limit should be BHD 1,000 or higher.",
          "يجب ألا يقل مبلغ التأمين عن 1,000 د.ب."
        )
      );
    } else if (numericValue && numericAmount > 25000) {
      setPolicyLimitError(
        translate(
          "Policy limit cannot exceed BHD 25,000.",
          "مبلغ التأمين لا يمكن أن يتجاوز 25,000 دينار بحريني."
        )
      );
    } else {
      setPolicyLimitError("");
    }

    const formattedValue = formatPolicyLimit(numericValue);
    let update = { ...values, [name]: formattedValue };
    setValues(update);
    if (errors?.[name]) {
      validateForm(update);
    }
  };

  return (
    <>
      <SignInModal
        show={showLoginModal}
        setShow={setShowLoginModal}
        cbSuccess={handleLoginSuccess}
      />
      <PersonalAccidentJourneyLayout>
        <div className="personalInfo max767:px-5 bg-white">
          <BottomStickyBtnLayout
            btn_onClick={formSubmitHandler}
            btn_label={translate("Continue", "التالي")}
            btn_disabled={isLoadingQuote || !quoteData || !!policyLimitError}
            show_summary={
              !!values.policy_limit &&
              !!values.occupation &&
              !isLoadingQuote &&
              !quoteHasFailed &&
              !!quoteData
            }
            price={quoteData?.totalPremium || 0}
            onShowSummary={handleShowSummary}
            isLoading={isLoadingQuote}
          >
            <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-5">
              <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left mb-2 max767:rtl:text-right">
                {translate("Required Information", "بياناتك")}
              </h1>
              <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left max767:rtl:text-right">
                {translate(
                  "Please enter the required information below.",
                  "يرجى إدخال المعلومات المطلوبة أدناه."
                )}
              </p>

              <form onSubmit={formSubmitHandler} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-base font-normal text-[#48535B] mb-1">
                    {translate("Policy Limit", "مبلغ التأمين")}{" "}
                    <span className="text-[#B0BABF]">
                      ({translate("max BHD 25,000", "الحد الاقصى : 25،000 د.ب")}
                      )
                    </span>
                  </label>
                  <Input
                    className="px-4 py-6 max767:items-center max767:w-full text-2xl not-italic font-normal"
                    endAdornment={
                      <div className="flex align-middle mt-1">
                        <img src={redflag} alt="flag" />
                        <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                          {translate("BHD", "د.ب")}
                        </p>
                      </div>
                    }
                    type="text"
                    name="policy_limit"
                    value={values.policy_limit || ""}
                    handleChange={handlePolicyLimitChange}
                    error={errors?.policy_limit}
                  />
                  {policyLimitError && (
                    <div className="mt-2 text-red-600 text-sm">
                      {policyLimitError}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <SelectInput
                    label={translate("Occupation", "المهنة")}
                    name="occupation"
                    formData={values}
                    handleChange={handleChange}
                    options_data={occupationOptions}
                    default_placeholder={
                      occupationsLoading
                        ? translate("Loading...", "جارٍ التحميل...")
                        : translate("Select Occupation", "اختر المهنة")
                    }
                    error={
                      errors?.occupation
                        ? translate(
                            "This field is required.",
                            "هذا الحقل مطلوب"
                          )
                        : ""
                    }
                    className="w-full"
                    disabled={occupationsLoading}
                  />
                </div>
              </form>
            </div>
          </BottomStickyBtnLayout>
        </div>
      </PersonalAccidentJourneyLayout>

      {/* Travel Summary Modal (Reused) */}
      <TravelSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        selectedPlan={{
          // Add quote data properties that TravelSummaryModal expects
          premium: quoteData?.premium || 0,
          tax: quoteData?.tax || 0,
          totalPremium: quoteData?.totalPremium || 0,
          quoteNumber: quoteData?.quoteNumber || quoteData?.policyNumber,
          apiData: quoteData,
          isRealApiData: true,
        }}
      />
    </>
  );
};

export default PersonalInfo;
